import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { EventResponseEvent } from '../../types/event-response.type';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { ResizeService } from '../../services/resize.service';

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventListComponent implements OnChanges, AfterViewChecked {
  @Input() events: EventResponseEvent[] = [];
  @Input() activeEventId: number | null = null;
  @Input() useDatesMarker: boolean = false;
  @Input() selectedDate: Date | undefined;
  @Input() showActiveEventInline: boolean = false;

  @Output() eventClicked: EventEmitter<EventResponseEvent> = new EventEmitter();
  @Output() goToEventPageClicked: EventEmitter<EventResponseEvent> =
    new EventEmitter<EventResponseEvent>();

  public changesDetected: boolean = false;
  private markers: Record<number, number> = {};

  public isMobile$ = this.resizeService.isMobile$;

  get shouldShowNoEventsInfo(): boolean {
    if (this.selectedDate && this.events.length > 0) {
      return !isSameDay(
        this.selectedDate,
        new Date(this.events[0]?.event_start)
      );
    }

    return false;
  }

  constructor(private resizeService: ResizeService) {}

  get selectedEvent(): EventResponseEvent | undefined {
    return this.showActiveEventInline
      ? this.events.find((event) => event.event_id === this.activeEventId)
      : undefined;
  }

  trackById(_: number, event: EventResponseEvent): number {
    return event.event_id;
  }

  dateFormatted(dateString: string): string {
    const date = new Date(dateString);

    if (Object.values(this.markers).indexOf(date.getTime()) === 0) {
      return format(date, 'MMM dd, yyy');
    }

    return format(date, 'MMMM yyy');
  }

  handleEventClick(event: EventResponseEvent, e: Event): void {
    this.eventClicked.emit(event);

    if (this.showActiveEventInline) {
      setTimeout(() => {
        (e.target as HTMLElement)
          .closest('.js-event-list-event')
          ?.scrollIntoView();
      }, 100);
    }
  }

  isActive(event: EventResponseEvent): boolean {
    return this.activeEventId === event.event_id;
  }

  ngAfterViewChecked(): void {
    this.markers = [];
  }

  ngOnChanges(): void {
    this.changesDetected = true;

    setTimeout(() => {
      this.changesDetected = false;
    }, 200);
  }

  shouldShowMarker(event: EventResponseEvent): boolean {
    if (!this.useDatesMarker) {
      return false;
    }

    const eventStartDate = new Date(event.event_start);

    if (Object.values(this.markers).length === 0) {
      this.markers[eventStartDate.getTime()] = eventStartDate.getTime();
      return true;
    } else {
      const eventStartDateMonthStart = new Date(
        format(eventStartDate, 'yyyy-MM-01')
      );

      const firstMarker = Object.values(this.markers)[0];

      if (
        !isSameMonth(firstMarker, eventStartDateMonthStart) &&
        !this.markers[eventStartDateMonthStart.getTime()]
      ) {
        this.markers[eventStartDateMonthStart.getTime()] =
          eventStartDateMonthStart.getTime();

        return true;
      }
    }

    return false;
  }
}
